import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from 'pencil/styles';
import CssBaseline from 'pencil/CssBaseline';
import LinearProgress from 'pencil/LinearProgress';
import withData from 'circe/Http/withData';
import classNames from 'classnames';
import * as I18n from 'circe/I18n';
import Container from 'pencil/Container';
import Notifications from 'circe/notifications/components/Notifications';
import clx from 'classnames';
import Header from 'domain/layout/components/Header';
import Sidebar from 'domain/layout/components/Sidebar';

import Main from 'domain/layout/components/Main';
import LoadingIndicator from 'domain/layout/components/LoadingIndicator';
import Footer from 'domain/layout/components/Footer';
import {
  isAllowedMenuItem,
  filterAndMergeNotifications,
  storeNotifications,
  addDefaultValuesToNotifications,
  getNotifications,
} from 'domain/layout/utils';
import InfoIcon from 'assets/img/infoIcon.svg';
import { Toaster } from 'react-hot-toast';
import SVG from 'react-inlinesvg';
import {
  renderAllNotifications,
  renderExpandedNotifications,
} from 'domain/layout/components/Notifications';

const drawerWidth = 240;

const styles = (theme) => ({
  container: {
    backgroundColor: theme.branding.background.primary,
    padding: '0px !important',
    maxWidth: 'calc(100% - 90px)',
    '@media only screen and (max-width: 600px)': {
      maxWidth: '100%',
    },
  },
  root: {
    display: 'flex',
  },
  whiteBackground: {
    background: '#fff',
  },
  drawerOpen: {
    width: `calc(100% - ${395}px)`,
  },
  rootLoader: {
    backgroundColor: '#2F3D49',
    marginTop: 80,
    width: '100%',
    marginBottom: -50,
    position: 'fixed',
  },
  rootNotification: {
    backgroundColor: '#fff',
    marginTop: 70,
    width: `calc(100% - ${theme.spacing(7) + 1}px)`,
    [theme.breakpoints.up('sm')]: {
      width: `calc(100% - ${theme.spacing(9) + 1}px)`,
    },
    marginBottom: -50,
    position: 'fixed',
    padding: 10,
    color: '#000',
    boxShadow: ' 0px 7px 10px #ccc',
    zIndex: 9,
    marginLeft: theme.spacing(9) + 1,
    paddingLeft: 30,
  },
  openedRootNotification: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
  },
  icon: {
    fontSize: 20,
  },
  iconVariant: {
    opacity: 0.9,
    marginRight: theme.spacing(1),
  },
  message: {
    display: 'flex',
    alignItems: 'center',
  },
  circularLoading: {
    margin: '0 auto',
  },
  loadingRouteContent: {
    minHeight: 'calc(100vh - 80px)',
    width: '100%',
    display: 'flex',
    flexFlow: 'column',
    textAlign: 'center',
    justifyContent: 'center',
  },
  closeButton: {
    color: '#2F80ED',
    position: 'absolute',
    top: '10px',
    right: '10px',
  },
  toast: {
    width: '384px',
    minHeight: '80px',
    borderRadius: '16px',
    color: '#4F4F4F',
  },
  tostBody: {
    margin: '0px 5px 0px 25px',
    width: '286px',
    fontSize: '14px',
    lineHeight: '17px',
  },
  notificationMessage: {
    margin: '5px',
  },
  showMoreBtn: {
    color: '#2F80ED',
    fontWeight: 'bold',
    padding: '0 2px',
  },
  closeToast: {
    position: 'absolute',
    top: '15px',
    right: '8px',
    width: '24px',
    height: '24px',
  },
  toastIcon: {
    position: 'absolute',
    top: '21px',
    left: '12px',
  },
});

const Layout = (props) => {
  const [open, setOpen] = useState(false);
  const [expandedNotifications, setExpandedNotifications] = useState([]);
  const [allNotifications, setAllNotifications] = useState([]);
  const [closedNotifications, setClosedNotifications] = useState([]);

  const {
    classes,
    theme,
    isLogged,
    logout,
    hideSidebar,
    authorities,
    canRoute,
    userData,
    isLoading,
    isAccountAdmin,
    isNotificationShowing = false,
    notifications,
    hideNotification,
    accountToken,
    currentPageTitle,
    setCurrentPageTitle,
    markNotificationAsClosed,
    markNotificationAsEntered,
    clearNotifications,
    newNotifications,
    activeNotifications,
  } = props;

  const prevNewNotificationsLength = useRef(newNotifications.length);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleExpandNotification = (notificationId) => {
    setExpandedNotifications((prevExpandedNotifications) => [
      ...prevExpandedNotifications,
      notificationId,
    ]);
  };

  useEffect(() => {
    if (newNotifications.length > prevNewNotificationsLength.current) {
      const processedNotifications = addDefaultValuesToNotifications(
        newNotifications,
        getNotifications(),
      );

      setAllNotifications(
        filterAndMergeNotifications(allNotifications, processedNotifications, closedNotifications),
      );

      storeNotifications(processedNotifications);

      prevNewNotificationsLength.current = newNotifications.length;
    }
  }, [newNotifications]);

  useEffect(() => {
    renderAllNotifications(
      classes,
      expandedNotifications,
      allNotifications,
      setAllNotifications,
      closedNotifications,
      handleExpandNotification,
      setClosedNotifications,
      markNotificationAsClosed,
    );
  }, [allNotifications]);

  useEffect(() => {
    renderExpandedNotifications(
      classes,
      expandedNotifications,
      allNotifications,
      setAllNotifications,
      closedNotifications,
      handleExpandNotification,
      setClosedNotifications,
      markNotificationAsClosed,
    );
  }, [expandedNotifications]);

  return (
    <>
      <div className={classes.root}>
        <Toaster
          gutter={10}
          reverseOrder
          toastOptions={{
            className: classes.toast,
            duration: Infinity,
            position: 'bottom-right',
          }}
        />
        <CssBaseline />
        {!hideSidebar && (
          <Header
            isLogged={isLogged}
            logout={logout}
            handleDrawerOpen={handleDrawerOpen}
            handleDrawerClose={handleDrawerClose}
            open={open}
            userData={userData}
            isAccountAdmin={isAccountAdmin}
            accountToken={accountToken}
            currentPageTitle={currentPageTitle}
            activeNotifications={activeNotifications}
            newNotifications={addDefaultValuesToNotifications(newNotifications, getNotifications())}
            clearNotifications={clearNotifications}
            prevNewNotificationsLength={prevNewNotificationsLength}
          />
        )}
        {!hideSidebar && (
          <Sidebar
            handleDrawerOpen={handleDrawerOpen}
            handleDrawerClose={handleDrawerClose}
            open={open}
            isAllowedMenuItem={isAllowedMenuItem}
            authorities={authorities}
            setCurrentPageTitle={setCurrentPageTitle}
            currentPageTitle={currentPageTitle}
            accountToken={accountToken}
          />
        )}
        {isLoading && (
          <LinearProgress
            variant="query"
            classes={{
              colorPrimary: classes.colorPrimary,
              colorSecondary: classes.colorSecondary,
              root: classes.rootLoader,
            }}
          />
        )}
        {isNotificationShowing && (
          <div
            className={classNames(classes.rootNotification, {
              [classes.openedRootNotification]: open,
            })}
          >
            Hey
          </div>
        )}
        <Container
          maxWidth={theme.layout.width}
          className={clx(classes.container, { [classes.drawerOpen]: open })}
        >
          {canRoute ? (
            <>
              <Main
                isLogged={isLogged}
                authorities={authorities}
                isNotificationShowing={isNotificationShowing}
              />
            </>
          ) : (
            <div className={classes.loadingRouteContent}>
              <LoadingIndicator />
            </div>
          )}
          <Footer>
            {I18n.str('copyWrites', '© Copyright 2018 - {currentYear} {companyName}', {
              currentYear: new Date().getFullYear(),
              companyName: theme.branding.company.name,
            })}
          </Footer>
        </Container>
      </div>
      <Notifications notifications={notifications} hide={hideNotification} />
    </>
  );
};

Layout.propTypes = {
  classes: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired,
};

const styled = withData({})(withStyles(styles, { withTheme: true })(Layout));
export default styled;
